import { useCallback, useEffect, useMemo, type CSSProperties, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Button,
  ErrorBanner,
  MetricStrip,
  PageHeader,
  Pill,
  ProgressBar,
  SprintList as FrmSprintList,
  StatusPill,
  Surface,
  TimelineVersions as FrmTimelineVersions,
  WorkbenchPanel,
} from '@frm/ui'

import {
  fetchReleases,
  type FeatureStatus,
  type ReleaseDto,
  type ReleaseFeatureDto,
  type ReleaseSprintDto,
  type ReleaseState,
  type RoadmapTaskState,
} from '../api/releases'
import { formatInteger, MONO_NUMERIC_STYLE } from '../shared/format'
import { InProgressAction } from '../shared/in-progress'

const PAGE_STYLE: CSSProperties = {
  display: 'grid',
  gap: 16,
  minWidth: 0,
}

const STACK_STYLE: CSSProperties = {
  display: 'grid',
  gap: 12,
}

const ROW_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  minWidth: 0,
}

const MUTED_STYLE: CSSProperties = {
  color: 'var(--frm-text-muted, #94a3b8)',
}

const RELEASE_STATE_META: Record<ReleaseState, { label: string; status: 'synced' | 'ok' | 'warning' | 'unknown'; tone: 'success' | 'info' | 'warning' | 'neutral' }> = {
  released: { label: 'выпущен', status: 'synced', tone: 'success' },
  active: { label: 'активен', status: 'ok', tone: 'info' },
  closing: { label: 'закрытие', status: 'warning', tone: 'warning' },
  planned: { label: 'в плане', status: 'unknown', tone: 'neutral' },
}

const FEATURE_META: Record<FeatureStatus, { label: string; tone: 'success' | 'info' | 'neutral'; order: number }> = {
  done: { label: 'Готово', tone: 'success', order: 2 },
  wip: { label: 'В работе', tone: 'info', order: 0 },
  plan: { label: 'В плане', tone: 'neutral', order: 1 },
}

const TASK_LABELS: Record<RoadmapTaskState, string> = {
  ' ': 'todo',
  '~': 'в работе',
  x: 'готово',
  '!': 'блок',
}

function releaseProgress(release: ReleaseDto): number {
  if (release.functions.length === 0) return 0
  const done = release.functions.filter((feature) => feature.status === 'done').length
  const wip = release.functions.filter((feature) => feature.status === 'wip').length
  return Math.round(((done + wip * 0.5) / release.functions.length) * 100)
}

function featureCounts(release: ReleaseDto) {
  return {
    done: release.functions.filter((feature) => feature.status === 'done').length,
    wip: release.functions.filter((feature) => feature.status === 'wip').length,
    plan: release.functions.filter((feature) => feature.status === 'plan').length,
  }
}

function pickDefaultRelease(releases: ReleaseDto[]): ReleaseDto | null {
  return releases.find((release) => release.state === 'active' || release.state === 'closing')
    ?? releases.find((release) => release.sprints.some((sprint) => sprint.state === 'active'))
    ?? releases.find((release) => release.state === 'planned')
    ?? releases[0]
    ?? null
}

function releaseByParam(releases: ReleaseDto[], selected: string | null): ReleaseDto | null {
  if (!selected) return null
  return releases.find((release) => release.v === selected) ?? null
}

function featureGroups(features: ReleaseFeatureDto[]): Array<{ status: FeatureStatus; items: ReleaseFeatureDto[] }> {
  const statuses = Object.keys(FEATURE_META) as FeatureStatus[]
  return statuses
    .sort((a, b) => FEATURE_META[a].order - FEATURE_META[b].order)
    .map((status) => ({
      status,
      items: features.filter((feature) => feature.status === status),
    }))
}

function featuresForSprint(release: ReleaseDto, sprintId: string): ReleaseFeatureDto[] {
  return release.functions.filter((feature) => feature.sprints.includes(sprintId))
}

function shortPromise(text: string): string {
  if (text.length <= 118) return text
  return `${text.slice(0, 115).trimEnd()}...`
}

function releaseSummary(release: ReleaseDto): string {
  const counts = featureCounts(release)
  return `${release.functions.length} функций · ${counts.done} готово · ${counts.wip} в работе · ${release.sprints.length} спринтов`
}

function TimelineVersions({
  releases,
  selected,
  onSelect,
}: {
  releases: ReleaseDto[]
  selected: string
  onSelect: (version: string) => void
}) {
  return (
    <WorkbenchPanel title="Версии продукта" description="Обещание, статус и готовность каждой версии.">
      <FrmTimelineVersions
        selectedId={selected}
        onSelect={onSelect}
        items={releases.map((release) => {
          const counts = featureCounts(release)
          const state = RELEASE_STATE_META[release.state]
          return {
            id: release.v,
            label: release.v,
            state: state.label,
            description: shortPromise(release.promise),
            progress: releaseProgress(release),
            progressLabel: `${counts.done}/${release.functions.length || 0}`,
            date: release.date || 'дата не задана',
            meta: `${release.sprints.length} спринтов`,
            ahead: (release.ahead?.commits ?? 0) > 0 ? `невыпущено +${release.ahead?.commits}` : undefined,
          }
        })}
        stateToneMap={{ выпущен: 'success', активен: 'info', закрытие: 'warning', 'в плане': 'neutral' }}
      />
    </WorkbenchPanel>
  )
}

function ReleaseHeader({ release, onOpenUnreleased }: { release: ReleaseDto; onOpenUnreleased: () => void }) {
  const pct = releaseProgress(release)
  const counts = featureCounts(release)
  const state = RELEASE_STATE_META[release.state]
  const ahead = release.ahead?.commits ?? 0
  return (
    <WorkbenchPanel
      title={`Релиз ${release.v}`}
      description={releaseSummary(release)}
    >
      <div style={STACK_STYLE}>
        <Surface padding="md" variant="subtle">
          <div style={STACK_STYLE}>
            <div style={ROW_STYLE}>
              <span style={MUTED_STYLE}>Обещание версии</span>
              <StatusPill status={state.status} label={state.label} />
            </div>
            <strong style={{ fontSize: 18, lineHeight: 1.45 }}>{release.promise}</strong>
          </div>
        </Surface>
        <div className="releases-header-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
          <Surface padding="sm" variant="subtle">
            <span style={MUTED_STYLE}>Дата</span>
            <div style={{ ...MONO_NUMERIC_STYLE, marginTop: 5 }}>{release.date || 'not set'}</div>
          </Surface>
          <Surface padding="sm" variant="subtle">
            <span style={MUTED_STYLE}>Готово</span>
            <div style={{ ...MONO_NUMERIC_STYLE, marginTop: 5 }}>{formatInteger(counts.done)}</div>
          </Surface>
          <Surface padding="sm" variant="subtle">
            <span style={MUTED_STYLE}>В работе</span>
            <div style={{ ...MONO_NUMERIC_STYLE, marginTop: 5 }}>{formatInteger(counts.wip)}</div>
          </Surface>
          <Surface padding="sm" variant="subtle">
            <span style={MUTED_STYLE}>В плане</span>
            <div style={{ ...MONO_NUMERIC_STYLE, marginTop: 5 }}>{formatInteger(counts.plan)}</div>
          </Surface>
        </div>
        <ProgressBar value={pct} max={100} tone="accent" ariaLabel="Готовность релиза" />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            size="sm"
            variant={ahead > 0 ? 'secondary' : 'ghost'}
            disabled={ahead === 0}
            onClick={onOpenUnreleased}
          >
            Невыпущено +{formatInteger(ahead)}
          </Button>
          <InProgressAction
            id="releases.promise.edit"
            render={(props) => (
              <Button
                {...props}
                onClick={(event) => props.onClick(event)}
                onKeyDown={(event) => props.onKeyDown(event)}
                size="sm"
                variant="ghost"
              >
                Правки через decision-flow
              </Button>
            )}
          />
        </div>
      </div>
    </WorkbenchPanel>
  )
}

function FeatureCard({ feature }: { feature: ReleaseFeatureDto }) {
  const meta = FEATURE_META[feature.status]
  return (
    <Surface padding="md" variant="subtle">
      <div style={STACK_STYLE}>
        <div style={ROW_STYLE}>
          <Pill tone={meta.tone}>{meta.label}</Pill>
          <Pill tone="info">{feature.section ?? 'Без раздела'}</Pill>
        </div>
        <div style={STACK_STYLE}>
          <strong>{feature.name}</strong>
          <span style={MUTED_STYLE}>{feature.desc}</span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {feature.sprints.length === 0 ? <Pill tone="neutral">без спринта</Pill> : null}
          {feature.sprints.map((sprint) => (
            <Pill key={sprint} tone="neutral">{sprint}</Pill>
          ))}
        </div>
        <Pill tone="neutral">правки отдельно</Pill>
      </div>
    </Surface>
  )
}

function ReleaseBoard({ features }: { features: ReleaseFeatureDto[] }) {
  const groups = featureGroups(features)
  if (features.length === 0) {
    return (
      <Surface padding="md" variant="subtle">
        <span style={MUTED_STYLE}>Функции для этой версии ещё не описаны в release map.</span>
      </Surface>
    )
  }
  return (
    <div className="releases-feature-board" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
      {groups.map((group) => {
        const meta = FEATURE_META[group.status]
        return (
          <Surface key={group.status} padding="md" variant="subtle">
            <div style={STACK_STYLE}>
              <div style={ROW_STYLE}>
                <Pill tone={meta.tone}>{meta.label}</Pill>
                <span style={MONO_NUMERIC_STYLE}>{formatInteger(group.items.length)}</span>
              </div>
              {group.items.length === 0 ? (
                <span style={MUTED_STYLE}>Нет функций в этом состоянии.</span>
              ) : (
                <div style={STACK_STYLE}>
                  {group.items.map((feature) => (
                    <FeatureCard key={feature.id} feature={feature} />
                  ))}
                </div>
              )}
            </div>
          </Surface>
        )
      })}
    </div>
  )
}

function sprintMeta(release: ReleaseDto, sprint: { id: string }): ReactNode {
  const features = featuresForSprint(release, sprint.id)
  if (features.length === 0) return <Pill tone="neutral">без функции</Pill>
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {features.map((feature) => (
        <Pill key={feature.id} tone={FEATURE_META[feature.status].tone}>{feature.name}</Pill>
      ))}
    </div>
  )
}

function taskNote(task: ReleaseSprintDto['tasks'][number]): string {
  const label = TASK_LABELS[task.st]
  return task.note ? `${label} · ${task.note}` : label
}

function ReleaseSprints({ release }: { release: ReleaseDto }) {
  if (release.sprints.length === 0) {
    return (
      <Surface padding="md" variant="subtle">
        <span style={MUTED_STYLE}>Спринты к релизу ещё не привязаны.</span>
      </Surface>
    )
  }
  return (
    <FrmSprintList
      expandable
      density="compact"
      defaultExpandedIds={release.sprints.filter((sprint) => sprint.state === 'active').map((sprint) => sprint.id)}
      items={release.sprints.map((sprint) => ({
        id: sprint.id,
        state: sprint.state,
        goal: sprint.goal,
        done: sprint.done,
        total: sprint.total,
        tasks: sprint.tasks.map((task, index) => ({
          id: `${sprint.id}-${index}`,
          state: task.st,
          text: task.text,
          note: taskNote(task),
        })),
      }))}
      renderMeta={(item) => sprintMeta(release, item)}
      groupLabels={{ active: 'Активные', planned: 'Планируемые', closed: 'Завершённые' }}
    />
  )
}

function ReleasesSkeleton() {
  return (
    <Surface padding="md" variant="subtle">
      <span style={MUTED_STYLE}>Загружаем релизы...</span>
    </Surface>
  )
}

export function ReleasesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const selectedParam = searchParams.get('release')
  const releasesQuery = useQuery({
    queryKey: ['releases'],
    queryFn: fetchReleases,
  })
  const releases = releasesQuery.data ?? []
  const release = useMemo(
    () => releaseByParam(releases, selectedParam) ?? pickDefaultRelease(releases),
    [releases, selectedParam],
  )

  useEffect(() => {
    if (!release || selectedParam === release.v) return
    const next = new URLSearchParams(searchParams)
    next.set('release', release.v)
    setSearchParams(next, { replace: true })
  }, [release, searchParams, selectedParam, setSearchParams])

  const handleSelect = useCallback((version: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('release', version)
    setSearchParams(next)
  }, [searchParams, setSearchParams])

  const openUnreleased = useCallback(() => {
    navigate('/projects?focus=ahead')
  }, [navigate])

  const doneFeatures = release?.functions.filter((feature) => feature.status === 'done').length ?? 0
  const wipFeatures = release?.functions.filter((feature) => feature.status === 'wip').length ?? 0
  const aheadCommits = release?.ahead?.commits ?? 0

  return (
    <div style={PAGE_STYLE}>
      <PageHeader
        eyebrow="Версии и функции"
        title="Релизы"
        description={release ? `Выбрана версия ${release.v}: ${releaseSummary(release)}` : 'Карта версий, функций и связанных спринтов.'}
        meta={<Pill tone={releasesQuery.isFetching ? 'warning' : 'success'}>{releasesQuery.isFetching ? 'обновляем' : 'синхронизировано'}</Pill>}
      />
      <MetricStrip
        minColumnWidth={170}
        items={[
          { id: 'features', label: 'функций в версии', value: formatInteger(release?.functions.length ?? 0) },
          { id: 'done', label: 'готово', value: formatInteger(doneFeatures), tone: doneFeatures ? 'success' : 'neutral' },
          { id: 'wip', label: 'в работе', value: formatInteger(wipFeatures), tone: wipFeatures ? 'info' : 'neutral' },
          { id: 'ahead', label: 'невыпущено', value: `+${formatInteger(aheadCommits)}`, tone: aheadCommits ? 'warning' : 'neutral' },
        ]}
      />
      {releasesQuery.isError ? (
        <ErrorBanner
          title="Не удалось загрузить релизы"
          description={releasesQuery.error instanceof Error ? releasesQuery.error.message : String(releasesQuery.error)}
          action={<Button onClick={() => void releasesQuery.refetch()}>Повторить</Button>}
        />
      ) : null}
      {releasesQuery.isLoading ? <ReleasesSkeleton /> : null}
      {!releasesQuery.isLoading && releases.length === 0 ? (
        <Surface padding="md" variant="subtle">
          <span style={MUTED_STYLE}>Версии ещё не описаны в карте проекта.</span>
        </Surface>
      ) : null}
      {release ? (
        <>
          <TimelineVersions releases={releases} selected={release.v} onSelect={handleSelect} />
          <ReleaseHeader release={release} onOpenUnreleased={openUnreleased} />
          <div className="releases-detail-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.18fr) minmax(340px, 0.82fr)', gap: 16, alignItems: 'start' }}>
            <WorkbenchPanel title="Функции релиза" description="Что версия обещает продукту и какими спринтами это закрывается.">
              <ReleaseBoard features={release.functions} />
            </WorkbenchPanel>
            <WorkbenchPanel title="Спринты релиза" description="Задачи из roadmap отображаются без правки из этого экрана.">
              <ReleaseSprints release={release} />
            </WorkbenchPanel>
          </div>
        </>
      ) : null}
    </div>
  )
}
